import React from 'react';
import { TemplateFrame } from '../TemplateFrame';
import { Film, Camera, MapPin, Aperture, Download, Heart, ArrowRight, Check, Quote, Sparkles } from 'lucide-react';
// ---------- Reel cover safe-zone overlay ----------
function SafeZoneOverlay() {
  return <>
      <div className="absolute top-0 left-0 right-0 pointer-events-none" style={{
      height: '14%'
    }} data-id="element-670">
        <div className="w-full h-full border-b border-dashed border-film-red/40 bg-film-red/[0.04] flex items-end justify-end px-2 pb-0.5" data-id="element-671">
          <span className="font-mono text-[7px] text-film-red/70 uppercase tracking-wider" data-id="element-672">
            IG UI
          </span>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 pointer-events-none" style={{
      height: '20%'
    }} data-id="element-673">
        <div className="w-full h-full border-t border-dashed border-film-red/40 bg-film-red/[0.04] flex items-start justify-end px-2 pt-0.5" data-id="element-674">
          <span className="font-mono text-[7px] text-film-red/70 uppercase tracking-wider" data-id="element-675">
            IG UI
          </span>
        </div>
      </div>
    </>;
}
function SectionHeader({
  id,
  title,
  sub
}: {
  id: string;
  title: string;
  sub: string;
}) {
  return <div className="mb-8" data-id="element-676">
      <p className="font-mono text-[10px] uppercase tracking-wider text-film-gray mb-1" data-id="element-677">
        {id}
      </p>
      <h2 className="font-display font-bold text-2xl text-film-black" data-id="element-678">
        {title}
      </h2>
      <p className="text-sm text-film-gray mt-1 max-w-xl" data-id="element-679">{sub}</p>
    </div>;
}
// Reusable tiny logo
const FN_ICON = 'https://cdn.magicpatterns.com/uploads/6S42RJUThA4LVcV5MgRYxz/filmnotes-fn-full-icon_1024.png';
export function FeedTemplatesPage() {
  return <div className="max-w-6xl mx-auto py-12 px-8 space-y-20" data-id="element-680">
      {/* Header */}
      <div data-id="element-681">
        <p className="font-mono text-xs text-film-gray mb-3" data-id="element-682">
          02_TEMPLATES_FEED
        </p>
        <h1 className="font-display font-bold text-4xl text-film-black mb-2" data-id="element-683">
          Feed Templates
        </h1>
        <p className="text-film-gray max-w-2xl" data-id="element-684">
          Reel covers, carousels, and singles. Built for IG feed first — every
          frame respects safe zones and stays legible at 1080px.
        </p>
      </div>

      {/* =========================================== */}
      {/* REEL COVERS                                  */}
      {/* =========================================== */}
      <section data-id="element-685">
        <SectionHeader id="§ REEL_COVERS" title="Reel Covers" sub="9:16 · 1080×1920 · Dashed overlays mark where IG UI sits. Title and CTA must stay inside the inner band." data-id="element-686" />
        <div className="flex flex-wrap gap-7" data-id="element-687">
          {/* CAT_TIP — Sunny 16 (cream + orange accent) */}
          <TemplateFrame componentId="CAT_TIP / FMT_REEL_COVER" dimensions="1080×1920 · 9:16" caption="Save-worthy craft tip. Cream with a thin orange accent bar." width="240px" data-id="element-688">
            <div className="relative bg-film-cream w-full" style={{
            aspectRatio: '9/16'
          }} data-id="element-689">
              {/* amber accent bar */}
              <div className="absolute left-0 top-0 bottom-0" style={{
              width: '4px',
              backgroundColor: '#D9711F'
            }} data-id="element-690" />
              <SafeZoneOverlay data-id="element-691" />
              <div className="absolute inset-0 flex flex-col px-5" style={{
              paddingTop: '16%',
              paddingBottom: '22%'
            }} data-id="element-692">
                <div className="flex items-center justify-between mb-4" data-id="element-693">
                  <span className="font-mono text-[8px] uppercase tracking-wider text-white px-1.5 py-0.5" style={{
                  backgroundColor: '#D9711F'
                }} data-id="element-694">
                    PRO TIP · 03
                  </span>
                  <img src={FN_ICON} className="w-4 h-4" alt="" data-id="element-695" />
                </div>

                <div className="flex-1 flex items-center" data-id="element-696">
                  <h3 className="font-display font-bold text-film-black leading-[0.92] text-3xl" data-id="element-697">
                    Sunny 16
                    <br data-id="element-698" />
                    in 15&nbsp;sec.
                  </h3>
                </div>

                <div className="flex items-end justify-between" data-id="element-699">
                  <p className="font-mono text-[8px] text-film-gray leading-tight" data-id="element-700">
                    EXPOSURE
                    <br data-id="element-701" />
                    BASICS
                  </p>
                  <span className="font-mono text-[8px] text-film-red uppercase tracking-wider" data-id="element-702">
                    save →
                  </span>
                </div>
              </div>
            </div>
          </TemplateFrame>

          {/* CAT_FEATURE — Track Every Roll (black bg) */}
          <TemplateFrame componentId="CAT_FEATURE / FMT_REEL_COVER" dimensions="1080×1920 · 9:16" caption="Product launch beat. Inverted to film-black for contrast." width="240px" data-id="element-703">
            <div className="relative bg-film-black w-full" style={{
            aspectRatio: '9/16'
          }} data-id="element-704">
              <SafeZoneOverlay data-id="element-705" />
              <div className="absolute inset-0 flex flex-col px-5" style={{
              paddingTop: '16%',
              paddingBottom: '22%'
            }} data-id="element-706">
                <div className="flex items-center justify-between mb-4" data-id="element-707">
                  <span className="font-mono text-[8px] uppercase tracking-wider text-white px-1.5 py-0.5" style={{
                  backgroundColor: '#8B2E3F'
                }} data-id="element-708">
                    NEW
                  </span>
                  <span className="font-mono text-[8px] text-white/40" data-id="element-709">
                    v2.4
                  </span>
                </div>

                {/* Phone mockup */}
                <div className="flex-1 flex items-center justify-center" data-id="element-710">
                  <div className="bg-film-cream rounded-[10px] w-[58%] aspect-[9/16] relative overflow-hidden border border-white/10" data-id="element-711">
                    <div className="px-2 pt-3" data-id="element-712">
                      <div className="h-1.5 w-6 bg-film-black/10 rounded-full mb-2" data-id="element-713" />
                      <div className="h-2 w-full bg-film-black/10 rounded-sm mb-1" data-id="element-714" />
                      <div className="h-2 w-3/4 bg-film-black/10 rounded-sm" data-id="element-715" />
                    </div>
                    <div className="absolute bottom-2 left-2 right-2 bg-white rounded p-1.5 flex items-center gap-1.5 border border-film-border" data-id="element-716">
                      <div className="w-1.5 h-1.5 rounded-full" style={{
                      backgroundColor: '#2D7A4F'
                    }} data-id="element-717" />
                      <span className="font-mono text-[6px] text-film-black" data-id="element-718">
                        PORTRA 400 · LOADED
                      </span>
                    </div>
                  </div>
                </div>

                <div data-id="element-719">
                  <h3 className="font-display font-bold text-white leading-[0.95] text-2xl" data-id="element-720">
                    Track every
                    <br data-id="element-721" />
                    roll.
                  </h3>
                  <p className="font-mono text-[8px] text-white/50 mt-2 uppercase tracking-wider" data-id="element-722">
                    Quicklog · 5&nbsp;seconds
                  </p>
                </div>
              </div>
            </div>
          </TemplateFrame>

          {/* CAT_COMMUNITY — full-bleed photo */}
          <TemplateFrame componentId="CAT_COMMUNITY / FMT_REEL_COVER" dimensions="1080×1920 · 9:16" caption="Full-bleed scan, gradient at base, white wordmark." width="240px" data-id="element-723">
            <div className="relative w-full bg-film-black" style={{
            aspectRatio: '9/16'
          }} data-id="element-724">
              <img src="https://images.unsplash.com/photo-1514539079130-25950c84af65?w=600&q=80" alt="" className="absolute inset-0 w-full h-full object-cover" data-id="element-725" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-black/30" data-id="element-726" />
              <SafeZoneOverlay data-id="element-727" />
              <div className="absolute inset-0 flex flex-col px-5" style={{
              paddingTop: '16%',
              paddingBottom: '22%'
            }} data-id="element-728">
                <div data-id="element-729">
                  <span className="font-mono text-[8px] uppercase tracking-wider bg-white text-film-black px-1.5 py-0.5" data-id="element-730">
                    COMMUNITY · 47
                  </span>
                </div>
                <div className="flex-1" data-id="element-731" />
                <div data-id="element-732">
                  <h3 className="font-display font-bold text-white leading-[0.95] text-2xl" data-id="element-733">
                    Tokyo,
                    <br data-id="element-734" />
                    at night.
                  </h3>
                  <p className="font-mono text-[8px] text-white/80 mt-2 uppercase tracking-wider" data-id="element-735">
                    @sarah_analog · CINESTILL 800T · M6
                  </p>
                </div>
              </div>
            </div>
          </TemplateFrame>

          {/* NEW · CAT_GEAR — FM2 editorial */}
          <TemplateFrame componentId="CAT_GEAR / FMT_REEL_COVER" dimensions="1080×1920 · 9:16" caption="Editorial gear cover. One word does the work." width="240px" data-id="element-736">
            <div className="relative bg-film-cream w-full" style={{
            aspectRatio: '9/16'
          }} data-id="element-737">
              <SafeZoneOverlay data-id="element-738" />
              <div className="absolute inset-0 flex flex-col px-5" style={{
              paddingTop: '16%',
              paddingBottom: '22%'
            }} data-id="element-739">
                <p className="font-mono text-[8px] uppercase tracking-wider text-film-gray" data-id="element-740">
                  Gear · field notes
                </p>

                <div className="mt-2" data-id="element-741">
                  <h3 className="font-display font-bold text-film-black leading-[0.8]" style={{
                  fontSize: '64px',
                  letterSpacing: '-0.04em'
                }} data-id="element-742">
                    FM2
                  </h3>
                  <div className="h-px bg-film-black mt-3 mb-3 w-12" data-id="element-743" />
                  <p className="font-display font-bold text-film-black text-sm leading-tight" data-id="element-744">
                    30 days,
                    <br data-id="element-745" />
                    12 rolls.
                  </p>
                </div>

                <div className="flex-1" data-id="element-746" />

                <div className="space-y-0.5" data-id="element-747">
                  <div className="flex justify-between font-mono text-[8px]" data-id="element-748">
                    <span className="text-film-gray" data-id="element-749">MOUNT</span>
                    <span className="text-film-black" data-id="element-750">NIKON F</span>
                  </div>
                  <div className="flex justify-between font-mono text-[8px]" data-id="element-751">
                    <span className="text-film-gray" data-id="element-752">MODE</span>
                    <span className="text-film-black" data-id="element-753">MANUAL</span>
                  </div>
                  <div className="flex justify-between font-mono text-[8px]" data-id="element-754">
                    <span className="text-film-gray" data-id="element-755">SHUTTER</span>
                    <span className="text-film-black" data-id="element-756">1/4000</span>
                  </div>
                </div>
              </div>
            </div>
          </TemplateFrame>
        </div>
      </section>

      {/* =========================================== */}
      {/* CAROUSEL · APP FEATURE                       */}
      {/* =========================================== */}
      <CarouselSection id="§ CAROUSEL_APP_FEATURE" title="Carousel · App Feature Walkthrough" sub="4:5 · 1080×1350 · 6 slides · alternating cream/white bodies, black cover, red CTA." slides={appFeatureSlides} catTag="FEATURE" catTagBgHex="#8B2E3F" idPrefix="CAT_FEATURE" count={6} data-id="element-757" />

      {/* =========================================== */}
      {/* CAROUSEL · GEAR GUIDE                        */}
      {/* =========================================== */}
      <CarouselSection id="§ CAROUSEL_GEAR_GUIDE" title="Carousel · Gear Guide" sub="4:5 · 6 slides · best 35mm under $200. Refined dark camera plates, crisper specs." slides={gearGuideSlides} catTag="GEAR" catTagBgHex="#2D7A4F" idPrefix="CAT_GEAR" count={6} data-id="element-758" />

      {/* =========================================== */}
      {/* CAROUSEL · LAB SPOTLIGHT                     */}
      {/* =========================================== */}
      <CarouselSection id="§ CAROUSEL_LAB_SPOTLIGHT" title="Carousel · Lab Spotlight" sub="4:5 · 5 slides · cover · map · services · hours · CTA. Photo Works Belfast." slides={labSpotlightSlides} catTag="LAB" catTagBgHex="#2E5C8A" idPrefix="CAT_LAB" count={5} data-id="element-759" />

      {/* =========================================== */}
      {/* NEW · CAROUSEL · TIP                          */}
      {/* =========================================== */}
      <CarouselSection id="§ CAROUSEL_TIP" title="Carousel · 5 Ways to Get Sharper Scans" sub="4:5 · 5 slides · large numerals, tactical advice. CAT_TIP staple format." slides={tipCarouselSlides} catTag="TIP" catTagBgHex="#D9711F" idPrefix="CAT_TIP" count={5} data-id="element-760" />

      {/* =========================================== */}
      {/* SINGLE POSTS                                 */}
      {/* =========================================== */}
      <section data-id="element-761">
        <SectionHeader id="§ SINGLE_POSTS" title="Single Posts · 1:1" sub="1080×1080 · Community scans, editorial pull-quotes, news drops, stock-of-the-week." data-id="element-762" />
        <div className="flex flex-wrap gap-7" data-id="element-763">
          {/* Portrait scan */}
          <TemplateFrame componentId="CAT_COMMUNITY / FMT_SINGLE · Portrait" dimensions="1080×1080 · 1:1" caption="Scan + cream credit strip with red accent bar." width="340px" data-id="element-764">
            <div className="relative bg-white" style={{
            aspectRatio: '1/1'
          }} data-id="element-765">
              <img src="https://images.unsplash.com/photo-1495121553079-4c61bcce1894?w=800&q=80" alt="" className="absolute inset-0 w-full h-full object-cover" data-id="element-766" />
              <div className="absolute bottom-0 left-0 right-0 bg-film-cream px-4 py-3 flex gap-3 items-start" data-id="element-767">
                <div className="w-1 self-stretch shrink-0" style={{
                backgroundColor: '#5B3A82'
              }} data-id="element-768" />
                <div className="flex-1 min-w-0" data-id="element-769">
                  <p className="font-display font-bold text-sm text-film-black leading-tight mb-0.5" data-id="element-770">
                    "Morning fog."
                  </p>
                  <p className="font-mono text-[9px] text-film-gray uppercase tracking-wider" data-id="element-771">
                    @sarah_analog · PORTRA 400 · NIKON FM2
                  </p>
                  <p className="font-mono text-[8px] text-film-gray/70 mt-1" data-id="element-772">
                    Shared with permission. Tag #filmnotes to be featured.
                  </p>
                </div>
              </div>
            </div>
          </TemplateFrame>

          {/* Landscape split */}
          <TemplateFrame componentId="CAT_COMMUNITY / FMT_SINGLE · Landscape" dimensions="1080×1080 · 1:1" caption="Photo top, two-column metadata bottom." width="340px" data-id="element-773">
            <div className="relative bg-film-cream" style={{
            aspectRatio: '1/1'
          }} data-id="element-774">
              <img src="https://images.unsplash.com/photo-1452587925148-ce544e77e70d?w=800&q=80" alt="" className="absolute top-0 left-0 right-0 w-full h-1/2 object-cover" data-id="element-775" />
              <div className="absolute bottom-0 left-0 right-0 h-1/2 p-5 flex flex-col" data-id="element-776">
                <p className="font-mono text-[9px] uppercase tracking-wider text-film-gray mb-2" data-id="element-777">
                  Community · 048
                </p>
                <h3 className="font-display font-bold text-2xl text-film-black leading-[0.95]" data-id="element-778">
                  Coastline,
                  <br data-id="element-779" />
                  October.
                </h3>
                <div className="mt-auto grid grid-cols-2 gap-x-4 gap-y-1.5 text-[10px]" data-id="element-780">
                  <div data-id="element-781">
                    <p className="font-mono text-film-gray uppercase tracking-wider mb-0.5" data-id="element-782">
                      Photographer
                    </p>
                    <p className="font-mono text-film-black" data-id="element-783">@henri.shoots</p>
                  </div>
                  <div data-id="element-784">
                    <p className="font-mono text-film-gray uppercase tracking-wider mb-0.5" data-id="element-785">
                      Camera
                    </p>
                    <p className="font-mono text-film-black" data-id="element-786">Olympus OM-1</p>
                  </div>
                  <div data-id="element-787">
                    <p className="font-mono text-film-gray uppercase tracking-wider mb-0.5" data-id="element-788">
                      Stock
                    </p>
                    <p className="font-mono text-film-black" data-id="element-789">HP5 Plus</p>
                  </div>
                  <div data-id="element-790">
                    <p className="font-mono text-film-gray uppercase tracking-wider mb-0.5" data-id="element-791">
                      Push
                    </p>
                    <p className="font-mono" style={{
                    color: '#D9711F'
                  }} data-id="element-792">
                      +1 stop
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </TemplateFrame>

          {/* NEW · Pull-quote single (black) */}
          <TemplateFrame componentId="CAT_TIP / FMT_SINGLE · Pull-Quote" dimensions="1080×1080 · 1:1" caption="Editorial card. One sentence carries the whole post." width="340px" data-id="element-793">
            <div className="relative bg-film-black p-7 flex flex-col" style={{
            aspectRatio: '1/1'
          }} data-id="element-794">
              <Quote className="w-5 h-5" style={{
              color: '#D9711F'
            }} data-id="element-795" />
              <h3 className="font-display font-bold text-white leading-[1.05] text-2xl mt-4" data-id="element-796">
                Meter for the shadows. Develop for the highlights. The rest is
                weather.
              </h3>
              <div className="mt-auto flex items-end justify-between pt-6 border-t border-white/10" data-id="element-797">
                <p className="font-mono text-[9px] text-white/60 uppercase tracking-wider leading-tight" data-id="element-798">
                  Ansel Adams
                  <br data-id="element-799" />
                  <span className="text-white/30" data-id="element-800">paraphrased</span>
                </p>
                <img src={FN_ICON} className="w-5 h-5 opacity-80" alt="" data-id="element-801" />
              </div>
            </div>
          </TemplateFrame>

          {/* News of May (fixed) */}
          <TemplateFrame componentId="CAT_FEATURE / FMT_SINGLE · News" dimensions="1080×1080 · 1:1" caption="Monthly editorial master. Eyebrow + month + 3 updates." width="340px" data-id="element-802">
            <div className="relative bg-film-cream p-6 flex flex-col" style={{
            aspectRatio: '1/1'
          }} data-id="element-803">
              <div className="flex items-center justify-between" data-id="element-804">
                <p className="font-mono text-[9px] uppercase tracking-wider text-film-gray" data-id="element-805">
                  News of the month
                </p>
                <p className="font-mono text-[9px]" style={{
                color: '#8B2E3F'
              }} data-id="element-806">
                  05 · 2026
                </p>
              </div>

              <p className="font-display font-bold leading-[0.85] mt-3" style={{
              fontSize: '56px',
              letterSpacing: '-0.04em',
              color: '#8B2E3F'
            }} data-id="element-807">
                MAY
              </p>

              <h3 className="font-display font-bold text-xl text-film-black leading-[1.05] mt-3" data-id="element-808">
                Lab finder, now
                <br data-id="element-809" />
                in 12 cities.
              </h3>

              <ul className="space-y-2 text-[11px] text-film-black mt-4" data-id="element-810">
                {['Madrid, Paris, London, Berlin added.', 'Same-day service filters.', 'Push/pull metadata sticks to the roll.'].map(item => <li key={item} className="flex gap-2 items-start" data-id="element-811">
                    <Check className="w-3 h-3 shrink-0 mt-0.5" style={{
                  color: '#8B2E3F'
                }} data-id="element-812" />
                    <span data-id="element-813">{item}</span>
                  </li>)}
              </ul>

              <div className="mt-auto pt-3 border-t border-film-border flex items-center justify-between" data-id="element-814">
                <p className="font-mono text-[9px] text-film-gray uppercase tracking-wider" data-id="element-815">
                  Full changelog →
                </p>
                <ArrowRight className="w-3.5 h-3.5" style={{
                color: '#8B2E3F'
              }} data-id="element-816" />
              </div>
            </div>
          </TemplateFrame>

          {/* NEW · Stock of the Week — split layout */}
          <TemplateFrame componentId="CAT_GEAR / FMT_SINGLE · Stock of the Week" dimensions="1080×1080 · 1:1" caption="Color-block left, tasting notes right. Editorial." width="340px" data-id="element-817">
            <div className="relative flex" style={{
            aspectRatio: '1/1'
          }} data-id="element-818">
              {/* Left color block · Kodak Yellow */}
              <div className="w-1/2 p-5 flex flex-col text-film-black" style={{
              backgroundColor: '#F0B92B'
            }} data-id="element-819">
                <p className="font-mono text-[9px] uppercase tracking-wider text-film-black/70" data-id="element-820">
                  Stock · 24
                </p>
                <div className="flex-1 flex items-end" data-id="element-821">
                  <h3 className="font-display font-bold leading-[0.85]" style={{
                  fontSize: '34px',
                  letterSpacing: '-0.02em'
                }} data-id="element-822">
                    PORTRA
                    <br data-id="element-823" />
                    400
                  </h3>
                </div>
                <p className="font-mono text-[9px] text-film-black/70 uppercase tracking-wider mt-3" data-id="element-824">
                  Kodak · C-41
                </p>
              </div>

              {/* Right notes */}
              <div className="w-1/2 bg-white p-5 flex flex-col" data-id="element-825">
                <p className="font-mono text-[9px] uppercase tracking-wider text-film-gray mb-3" data-id="element-826">
                  Tasting notes
                </p>
                <div className="space-y-2.5 text-[10px]" data-id="element-827">
                  <div data-id="element-828">
                    <p className="font-mono text-film-gray uppercase tracking-wider text-[8px] mb-0.5" data-id="element-829">
                      ISO
                    </p>
                    <p className="font-mono text-film-black" data-id="element-830">400 · push 800</p>
                  </div>
                  <div data-id="element-831">
                    <p className="font-mono text-film-gray uppercase tracking-wider text-[8px] mb-0.5" data-id="element-832">
                      Grain
                    </p>
                    <p className="font-mono text-film-black" data-id="element-833">Fine</p>
                  </div>
                  <div data-id="element-834">
                    <p className="font-mono text-film-gray uppercase tracking-wider text-[8px] mb-0.5" data-id="element-835">
                      Best for
                    </p>
                    <p className="font-mono text-film-black leading-tight" data-id="element-836">
                      Portraits, golden hour, soft skin tones
                    </p>
                  </div>
                  <div data-id="element-837">
                    <p className="font-mono text-film-gray uppercase tracking-wider text-[8px] mb-0.5" data-id="element-838">
                      Also try
                    </p>
                    <p className="font-mono text-film-red" data-id="element-839">Pro 400H</p>
                  </div>
                </div>
                <div className="mt-auto pt-3 border-t border-film-border" data-id="element-840">
                  <p className="font-mono text-[8px] text-film-gray uppercase tracking-wider" data-id="element-841">
                    Save · log · shoot
                  </p>
                </div>
              </div>
            </div>
          </TemplateFrame>
        </div>
      </section>

      {/* =========================================== */}
      {/* SINGLES · 4:5 PORTRAIT                       */}
      {/* =========================================== */}
      <section data-id="element-842">
        <SectionHeader id="§ SINGLES_4_5" title="Single Posts · 4:5 Portrait" sub="1080×1350 · Taller crop, more vertical real estate. Use to spotlight tips, stock, and community heroes with the tertiary palette." data-id="element-843" />
        <div className="flex flex-wrap gap-7" data-id="element-844">
          {/* CAT_TIP · Pro Tip 04 — Safelight Amber */}
          <TemplateFrame componentId="CAT_TIP / FMT_SINGLE_4_5 · Pro Tip" dimensions="1080×1350 · 4:5" caption="Save-worthy tip card. Safelight Amber takes the page." width="300px" data-id="element-845">
            <div className="relative w-full flex flex-col p-6 text-white" style={{
            aspectRatio: '4/5',
            backgroundColor: '#D9711F'
          }} data-id="element-846">
              <div className="flex items-center justify-between" data-id="element-847">
                <span className="font-mono text-[9px] uppercase tracking-wider text-white/80" data-id="element-848">
                  Pro Tip · 04
                </span>
                <img src={FN_ICON} className="w-4 h-4" alt="" data-id="element-849" />
              </div>

              <div className="flex-1 flex items-center mt-4" data-id="element-850">
                <h3 className="font-display font-bold leading-[0.92]" style={{
                fontSize: '34px',
                letterSpacing: '-0.02em'
              }} data-id="element-851">
                  Meter for
                  <br data-id="element-852" />
                  the shadows.
                </h3>
              </div>

              <div className="space-y-2 mt-2" data-id="element-853">
                <p className="text-[11px] text-white/85 leading-snug" data-id="element-854">
                  Negative film holds highlights better than shadows. Expose for
                  the dark zones, develop normally.
                </p>
                <div className="pt-3 border-t border-white/20 flex items-center justify-between" data-id="element-855">
                  <span className="font-mono text-[9px] text-white/80 uppercase tracking-wider" data-id="element-856">
                    Exposure basics
                  </span>
                  <span className="font-mono text-[9px] text-white uppercase tracking-wider" data-id="element-857">
                    Save →
                  </span>
                </div>
              </div>
            </div>
          </TemplateFrame>

          {/* CAT_GEAR · Stock Highlight — Kodak Yellow block */}
          <TemplateFrame componentId="CAT_GEAR / FMT_SINGLE_4_5 · Stock Highlight" dimensions="1080×1350 · 4:5" caption="Stock spotlight. Kodak Yellow top block, technical specs below." width="300px" data-id="element-858">
            <div className="relative bg-white w-full flex flex-col" style={{
            aspectRatio: '4/5'
          }} data-id="element-859">
              {/* Kodak Yellow top block */}
              <div className="px-5 pt-5 pb-4 text-film-black flex flex-col" style={{
              backgroundColor: '#F0B92B',
              height: '52%'
            }} data-id="element-860">
                <div className="flex items-center justify-between" data-id="element-861">
                  <span className="font-mono text-[9px] uppercase tracking-wider text-film-black/70" data-id="element-862">
                    Stock · 25
                  </span>
                  <span className="font-mono text-[9px] uppercase tracking-wider bg-film-black/10 px-1.5 py-0.5 text-film-black" data-id="element-863">
                    Push +1
                  </span>
                </div>

                <div className="flex-1 flex items-end" data-id="element-864">
                  <h3 className="font-display font-bold leading-[0.85]" style={{
                  fontSize: '38px',
                  letterSpacing: '-0.025em'
                }} data-id="element-865">
                    PORTRA
                    <br data-id="element-866" />
                    400
                  </h3>
                </div>

                <p className="font-mono text-[9px] text-film-black/70 uppercase tracking-wider mt-3" data-id="element-867">
                  Kodak · C-41 · 35mm
                </p>
              </div>

              {/* Specs */}
              <div className="flex-1 px-5 py-4 flex flex-col" data-id="element-868">
                <p className="font-mono text-[9px] uppercase tracking-wider text-film-gray mb-3" data-id="element-869">
                  Tasting notes
                </p>
                <div className="space-y-1.5 text-[10px] flex-1" data-id="element-870">
                  {[['ISO', '400 · pushable to 800'], ['Grain', 'Fine'], ['Best for', 'Portraits, golden hour'], ['Also try', 'Pro 400H · Gold 200']].map(([k, v]) => <div key={k} className="flex justify-between border-b border-film-border/60 pb-1" data-id="element-871">
                      <span className="text-film-gray font-mono uppercase tracking-wider text-[8px]" data-id="element-872">
                        {k}
                      </span>
                      <span className="text-film-black font-mono text-right" data-id="element-873">
                        {v}
                      </span>
                    </div>)}
                </div>
                <p className="font-mono text-[8px] uppercase tracking-wider mt-2 text-film-black" data-id="element-874">
                  Save · log · shoot →
                </p>
              </div>
            </div>
          </TemplateFrame>

          {/* CAT_COMMUNITY · Hero — Kodachrome Violet bottom band */}
          <TemplateFrame componentId="CAT_COMMUNITY / FMT_SINGLE_4_5 · Hero" dimensions="1080×1350 · 4:5" caption="Community hero. Photo + Kodachrome Violet credit band." width="300px" data-id="element-875">
            <div className="relative bg-film-black w-full flex flex-col" style={{
            aspectRatio: '4/5'
          }} data-id="element-876">
              {/* Photo */}
              <div className="relative" style={{
              height: '70%'
            }} data-id="element-877">
                <img src="https://images.unsplash.com/photo-1502920917128-1aa500764cbd?w=800&q=80" alt="" className="absolute inset-0 w-full h-full object-cover" data-id="element-878" />
                <div className="absolute top-3 left-3 bg-white/95 backdrop-blur px-1.5 py-0.5" data-id="element-879">
                  <span className="font-mono text-[8px] uppercase tracking-wider text-film-black" data-id="element-880">
                    Community · 052
                  </span>
                </div>
              </div>

              {/* Violet band */}
              <div className="flex-1 px-5 py-4 text-white flex flex-col justify-center" style={{
              backgroundColor: '#5B3A82'
            }} data-id="element-881">
                <h3 className="font-display font-bold text-xl leading-[1.0] mb-2" data-id="element-882">
                  "Late shift,
                  <br data-id="element-883" />
                  Lisbon."
                </h3>
                <div className="flex items-end justify-between mt-2 pt-2 border-t border-white/15" data-id="element-884">
                  <div data-id="element-885">
                    <p className="font-mono text-[8px] uppercase tracking-wider text-white/70" data-id="element-886">
                      Photographer
                    </p>
                    <p className="font-mono text-[10px] text-white" data-id="element-887">
                      @marta.rolls
                    </p>
                  </div>
                  <div className="text-right" data-id="element-888">
                    <p className="font-mono text-[8px] uppercase tracking-wider text-white/70" data-id="element-889">
                      Stock
                    </p>
                    <p className="font-mono text-[10px] text-white" data-id="element-890">
                      Cinestill 800T
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </TemplateFrame>
        </div>
      </section>
    </div>;
}
/* ============================================================ */
/* CAROUSEL SECTION COMPONENT                                    */
/* ============================================================ */
interface SlideData {
  n: number;
  bg: 'cream' | 'white' | 'black' | 'red';
  render: () => React.ReactNode;
}
function CarouselSection({
  id,
  title,
  sub,
  slides,
  catTag,
  catTagBgHex,
  idPrefix,
  count
}: {
  id: string;
  title: string;
  sub: string;
  slides: SlideData[];
  catTag: string;
  catTagBgHex: string;
  idPrefix: string;
  count: number;
}) {
  return <section data-id="element-891">
      <SectionHeader id={id} title={title} sub={sub} data-id="element-892" />
      <div className="overflow-x-auto pb-4 -mx-2 px-2" data-id="element-893">
        <div className="flex gap-5" style={{
        minWidth: 'max-content'
      }} data-id="element-894">
          {slides.map(s => <TemplateFrame key={s.n} componentId={`${idPrefix} / FMT_CAROUSEL_${s.n}`} dimensions={`Slide ${s.n}/${count} · 4:5`} width="240px" data-id="element-895">
              <div className={`relative w-full ${bgClass(s.bg)} flex flex-col`} style={{
            aspectRatio: '4/5'
          }} data-id="element-896">
                {/* Slide chrome (page number + cat tag) */}
                <div className="flex items-center justify-between px-4 pt-3 pb-2" data-id="element-897">
                  <span className={`font-mono text-[8px] ${s.bg === 'black' || s.bg === 'red' ? 'text-white/50' : 'text-film-gray'}`} data-id="element-898">
                    {s.n.toString().padStart(2, '0')}/
                    {count.toString().padStart(2, '0')}
                  </span>
                  <span className="font-mono text-[8px] uppercase tracking-wider text-white px-1.5 py-0.5" style={{
                backgroundColor: catTagBgHex
              }} data-id="element-899">
                    {catTag}
                  </span>
                </div>
                {/* Body */}
                <div className="flex-1 flex flex-col px-4 pb-4 min-h-0" data-id="element-900">
                  {s.render()}
                </div>
              </div>
            </TemplateFrame>)}
        </div>
      </div>
    </section>;
}
function bgClass(bg: 'cream' | 'white' | 'black' | 'red') {
  switch (bg) {
    case 'cream':
      return 'bg-film-cream';
    case 'white':
      return 'bg-white';
    case 'black':
      return 'bg-film-black text-white';
    case 'red':
      return 'bg-film-red text-white';
  }
}
/* ============================================================ */
/* SLIDE DATA                                                    */
/* ============================================================ */
const appFeatureSlides: SlideData[] = [{
  n: 1,
  bg: 'black',
  render: () => <div className="flex-1 flex flex-col justify-center" data-id="element-901">
        <p className="font-mono text-[8px] uppercase tracking-wider text-white/50 mb-2" data-id="element-902">
          Walkthrough
        </p>
        <h3 className="font-display font-bold text-2xl text-white leading-[0.95]" data-id="element-903">
          Track every
          <br data-id="element-904" />
          roll.
        </h3>
        <p className="text-[10px] text-white/60 mt-2 leading-snug" data-id="element-905">
          6 slides on the QuickLog flow.
        </p>
        <p className="font-mono text-[8px] text-film-red mt-4 uppercase tracking-wider" data-id="element-906">
          Swipe →
        </p>
      </div>
}, {
  n: 2,
  bg: 'cream',
  render: () => <div className="flex-1 flex flex-col" data-id="element-907">
        <div className="bg-white border border-film-border aspect-square flex items-center justify-center mb-3" data-id="element-908">
          <Film className="w-7 h-7 text-film-red" strokeWidth={1.5} data-id="element-909" />
        </div>
        <p className="font-mono text-[8px] uppercase tracking-wider text-film-gray mb-1" data-id="element-910">
          Step 01
        </p>
        <h3 className="font-display font-bold text-sm text-film-black leading-tight mb-1.5" data-id="element-911">
          Log a roll in 5 seconds.
        </h3>
        <p className="text-[9px] text-film-gray leading-snug" data-id="element-912">
          Tap +, pick stock, name it. Done.
        </p>
      </div>
}, {
  n: 3,
  bg: 'white',
  render: () => <div className="flex-1 flex flex-col" data-id="element-913">
        <div className="bg-film-cream border border-film-border aspect-square flex items-center justify-center mb-3" data-id="element-914">
          <Camera className="w-7 h-7 text-film-black" strokeWidth={1.5} data-id="element-915" />
        </div>
        <p className="font-mono text-[8px] uppercase tracking-wider text-film-gray mb-1" data-id="element-916">
          Step 02
        </p>
        <h3 className="font-display font-bold text-sm text-film-black leading-tight mb-1.5" data-id="element-917">
          Mark exposures, one tap.
        </h3>
        <p className="text-[9px] text-film-gray leading-snug" data-id="element-918">
          Tap the frame number after each shutter press.
        </p>
      </div>
}, {
  n: 4,
  bg: 'cream',
  render: () => <div className="flex-1 flex flex-col" data-id="element-919">
        <div className="bg-white border border-film-border aspect-square flex items-center justify-center mb-3" data-id="element-920">
          <Aperture className="w-7 h-7 text-film-orange" strokeWidth={1.5} data-id="element-921" />
        </div>
        <p className="font-mono text-[8px] uppercase tracking-wider text-film-gray mb-1" data-id="element-922">
          Step 03
        </p>
        <h3 className="font-display font-bold text-sm text-film-black leading-tight mb-1.5" data-id="element-923">
          Push/pull, auto-saved.
        </h3>
        <p className="text-[9px] text-film-gray leading-snug" data-id="element-924">
          Notes follow each roll to the lab.
        </p>
      </div>
}, {
  n: 5,
  bg: 'white',
  render: () => <div className="flex-1 flex flex-col" data-id="element-925">
        <div className="bg-film-cream border border-film-border aspect-square flex items-center justify-center mb-3" data-id="element-926">
          <MapPin className="w-7 h-7 text-film-red" strokeWidth={1.5} data-id="element-927" />
        </div>
        <p className="font-mono text-[8px] uppercase tracking-wider text-film-gray mb-1" data-id="element-928">
          Step 04
        </p>
        <h3 className="font-display font-bold text-sm text-film-black leading-tight mb-1.5" data-id="element-929">
          Find your lab.
        </h3>
        <p className="text-[9px] text-film-gray leading-snug" data-id="element-930">
          Filter by service, hours, distance.
        </p>
      </div>
}, {
  n: 6,
  bg: 'red',
  render: () => <div className="flex-1 flex flex-col justify-center items-center text-center" data-id="element-931">
        <img src={FN_ICON} className="w-9 h-9 mb-3 brightness-0 invert" alt="" data-id="element-932" />
        <h3 className="font-display font-bold text-xl text-white leading-[0.95] mb-4" data-id="element-933">
          Get
          <br data-id="element-934" />
          Film Notes.
        </h3>
        <div className="border border-white/70 text-white text-[9px] font-mono px-3 py-1.5 w-full text-center mb-1.5 uppercase tracking-wider" data-id="element-935">
          App Store
        </div>
        <div className="border border-white/70 text-white text-[9px] font-mono px-3 py-1.5 w-full text-center uppercase tracking-wider" data-id="element-936">
          Google Play
        </div>
      </div>
}];
const gearGuideSlides: SlideData[] = [{
  n: 1,
  bg: 'cream',
  render: () => <div className="flex-1 flex flex-col justify-center" data-id="element-937">
        <p className="font-mono text-[8px] uppercase tracking-wider text-film-gray mb-1" data-id="element-938">
          Community picks
        </p>
        <h3 className="font-display font-bold text-2xl text-film-black leading-[0.9] mb-2" data-id="element-939">
          Best 35mm
          <br data-id="element-940" />
          under $200.
        </h3>
        <p className="text-[10px] text-film-gray" data-id="element-941">
          Four cameras, real notes from real shooters.
        </p>
        <p className="font-mono text-[8px] text-film-red mt-4 uppercase tracking-wider" data-id="element-942">
          Swipe →
        </p>
      </div>
}, ...[{
  name: 'Pentax K1000',
  mount: 'Pentax K',
  mode: 'Manual',
  price: '$120'
}, {
  name: 'Canon AE-1',
  mount: 'Canon FD',
  mode: 'Program AE',
  price: '$180'
}, {
  name: 'Olympus OM-1',
  mount: 'Olympus OM',
  mode: 'Manual',
  price: '$160'
}, {
  name: 'Nikon FM2',
  mount: 'Nikon F',
  mode: 'Manual',
  price: '$200'
}].map((cam, i) => ({
  n: i + 2,
  bg: (i % 2 === 0 ? 'white' : 'cream') as 'white' | 'cream',
  render: () => <div className="flex-1 flex flex-col" data-id="element-943">
        <div className="aspect-square mb-3 flex items-center justify-center relative" style={{
      backgroundColor: '#3a3a3a'
    }} data-id="element-944">
          <Camera className="w-9 h-9 text-white/40" strokeWidth={1.25} data-id="element-945" />
          <div className="absolute inset-1 border border-white/5 pointer-events-none" data-id="element-946" />
        </div>
        <h3 className="font-display font-bold text-base text-film-black mb-2 leading-tight" data-id="element-947">
          {cam.name}
        </h3>
        <div className="space-y-0.5 text-[9px]" data-id="element-948">
          <div className="flex justify-between border-b border-film-border/60 pb-0.5" data-id="element-949">
            <span className="text-film-gray uppercase tracking-wider" data-id="element-950">
              Mount
            </span>
            <span className="text-film-black font-mono" data-id="element-951">{cam.mount}</span>
          </div>
          <div className="flex justify-between border-b border-film-border/60 pb-0.5" data-id="element-952">
            <span className="text-film-gray uppercase tracking-wider" data-id="element-953">
              Mode
            </span>
            <span className="text-film-black font-mono" data-id="element-954">{cam.mode}</span>
          </div>
          <div className="flex justify-between pt-0.5" data-id="element-955">
            <span className="text-film-gray uppercase tracking-wider" data-id="element-956">
              Price
            </span>
            <span className="text-film-red font-mono" data-id="element-957">{cam.price}</span>
          </div>
        </div>
      </div>
})), {
  n: 6,
  bg: 'black',
  render: () => <div className="flex-1 flex flex-col justify-center items-center text-center" data-id="element-958">
        <Heart className="w-7 h-7 text-film-red mb-3" data-id="element-959" />
        <h3 className="font-display font-bold text-lg text-white leading-[0.95] mb-2" data-id="element-960">
          Save
          <br data-id="element-961" />
          this list.
        </h3>
        <p className="text-[9px] text-white/60 leading-snug" data-id="element-962">
          Tap save · tag a friend who's looking.
        </p>
        <img src={FN_ICON} className="w-5 h-5 mt-4 brightness-0 invert opacity-60" alt="" data-id="element-963" />
      </div>
}];
const labSpotlightSlides: SlideData[] = [{
  n: 1,
  bg: 'cream',
  render: () => <div className="flex-1 flex flex-col" data-id="element-964">
        <div className="w-full mb-3 relative overflow-hidden" style={{
      height: '60px'
    }} data-id="element-965">
          <img src="https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=400&q=80" className="w-full h-full object-cover" alt="" data-id="element-966" />
        </div>
        <p className="font-mono text-[8px] uppercase tracking-wider text-film-gray mb-1" data-id="element-967">
          Lab Spotlight · 12
        </p>
        <h3 className="font-display font-bold text-xl text-film-black leading-[0.92] mb-2" data-id="element-968">
          Photo Works
          <br data-id="element-969" />
          Belfast.
        </h3>
        <p className="text-[10px] text-film-gray" data-id="element-970">est. 1987 · UK</p>
        <p className="font-mono text-[8px] text-film-red mt-auto uppercase tracking-wider" data-id="element-971">
          Swipe →
        </p>
      </div>
}, {
  n: 2,
  bg: 'white',
  render: () => <div className="flex-1 flex flex-col" data-id="element-972">
        <div className="aspect-square mb-3 relative overflow-hidden" style={{
      backgroundColor: '#F5E5E5'
    }} data-id="element-973">
          {/* abstract street grid */}
          <div className="absolute inset-0" data-id="element-974">
            <div className="absolute left-[20%] top-0 bottom-0 w-px bg-film-gray/30" data-id="element-975" />
            <div className="absolute left-[55%] top-0 bottom-0 w-px bg-film-gray/30" data-id="element-976" />
            <div className="absolute top-[30%] left-0 right-0 h-px bg-film-gray/30" data-id="element-977" />
            <div className="absolute top-[70%] left-0 right-0 h-px bg-film-gray/30" data-id="element-978" />
          </div>
          <MapPin className="absolute left-[45%] top-[35%] w-5 h-5 text-film-red" data-id="element-979" />
          <span className="absolute bottom-1 left-1.5 font-mono text-[7px] text-film-gray uppercase tracking-wider" data-id="element-980">
            BT1 2BA
          </span>
        </div>
        <p className="font-display font-bold text-sm text-film-black mb-0.5" data-id="element-981">
          Belfast, UK
        </p>
        <p className="font-mono text-[9px] text-film-gray leading-tight" data-id="element-982">
          12 High Street
        </p>
      </div>
}, {
  n: 3,
  bg: 'cream',
  render: () => <div className="flex-1 flex flex-col" data-id="element-983">
        <p className="font-mono text-[8px] uppercase tracking-wider text-film-gray mb-2" data-id="element-984">
          Services
        </p>
        <div className="flex flex-wrap gap-1 mb-3" data-id="element-985">
          {['C-41', 'E-6', 'B&W', 'Scan', 'Print'].map(srv => <span key={srv} className="bg-white border border-film-border text-[9px] font-mono px-2 py-0.5 text-film-black" data-id="element-986">
              {srv}
            </span>)}
        </div>
        <div className="mt-2 pt-2 border-t border-film-border space-y-1" data-id="element-987">
          <div className="flex justify-between font-mono text-[9px]" data-id="element-988">
            <span className="text-film-gray" data-id="element-989">C-41</span>
            <span className="text-film-black" data-id="element-990">24 hrs</span>
          </div>
          <div className="flex justify-between font-mono text-[9px]" data-id="element-991">
            <span className="text-film-gray" data-id="element-992">B&W</span>
            <span className="text-film-black" data-id="element-993">48 hrs</span>
          </div>
        </div>
        <p className="font-mono text-[8px] text-film-red mt-auto uppercase tracking-wider" data-id="element-994">
          Same-day · Tue–Sat
        </p>
      </div>
}, {
  n: 4,
  bg: 'white',
  render: () => <div className="flex-1 flex flex-col" data-id="element-995">
        <p className="font-mono text-[8px] uppercase tracking-wider text-film-gray mb-2" data-id="element-996">
          Hours
        </p>
        <div className="space-y-1.5 text-[10px]" data-id="element-997">
          {[['Tue–Fri', '10–18'], ['Sat', '10–16'], ['Sun–Mon', 'Closed']].map(([d, h]) => <div key={d} className="flex justify-between border-b border-film-border pb-1" data-id="element-998">
              <span className="text-film-gray uppercase tracking-wider font-mono text-[9px]" data-id="element-999">
                {d}
              </span>
              <span className="text-film-black font-mono" data-id="element-1000">{h}</span>
            </div>)}
        </div>
        <p className="font-mono text-[8px] text-film-red mt-auto uppercase tracking-wider" data-id="element-1001">
          Drop before Friday →
        </p>
      </div>
}, {
  n: 5,
  bg: 'black',
  render: () => <div className="flex-1 flex flex-col justify-center items-center text-center" data-id="element-1002">
        <MapPin className="w-7 h-7 text-film-red mb-3" data-id="element-1003" />
        <h3 className="font-display font-bold text-base text-white leading-[0.95] mb-3" data-id="element-1004">
          Find more labs
          <br data-id="element-1005" />
          in the app.
        </h3>
        <div className="bg-film-red text-white text-[9px] font-mono px-3 py-1.5 uppercase tracking-wider" data-id="element-1006">
          Get Film Notes
        </div>
      </div>
}];
const tipCarouselSlides: SlideData[] = [{
  n: 1,
  bg: 'cream',
  render: () => <div className="flex-1 flex flex-col justify-center" data-id="element-1007">
        <p className="font-mono text-[8px] uppercase tracking-wider text-film-gray mb-2" data-id="element-1008">
          Pro tip series · 07
        </p>
        <h3 className="font-display font-bold text-xl text-film-black leading-[0.92] mb-2" data-id="element-1009">
          5 ways to get
          <br data-id="element-1010" />
          sharper scans.
        </h3>
        <p className="text-[10px] text-film-gray" data-id="element-1011">
          Lab-tested. Works on most flatbeds & DSLR scan rigs.
        </p>
        <p className="font-mono text-[8px] text-film-orange mt-4 uppercase tracking-wider" data-id="element-1012">
          Swipe →
        </p>
      </div>
}, ...[{
  tip: 'Clean the negative.',
  body: 'Static brush + rocket blower. Dust is 80% of "soft" scans.'
}, {
  tip: 'Lock your focus.',
  body: 'Autofocus drifts on flat film. Manual focus on the grain.'
}, {
  tip: 'Match the resolution.',
  body: "No need to scan above 4000dpi for 35mm. You're scanning grain, not detail."
}].map((t, i) => ({
  n: i + 2,
  bg: (i % 2 === 0 ? 'white' : 'cream') as 'white' | 'cream',
  render: () => <div className="flex-1 flex flex-col" data-id="element-1013">
        <p className="font-display font-bold text-film-orange leading-none" style={{
      fontSize: '48px',
      letterSpacing: '-0.03em'
    }} data-id="element-1014">
          0{i + 1}
        </p>
        <div className="h-px bg-film-black mt-3 mb-3 w-8" data-id="element-1015" />
        <h3 className="font-display font-bold text-base text-film-black leading-tight mb-2" data-id="element-1016">
          {t.tip}
        </h3>
        <p className="text-[10px] text-film-gray leading-snug" data-id="element-1017">{t.body}</p>
      </div>
})), {
  n: 5,
  bg: 'black',
  render: () => <div className="flex-1 flex flex-col justify-center items-center text-center" data-id="element-1018">
        <Sparkles className="w-7 h-7 text-film-orange mb-3" data-id="element-1019" />
        <h3 className="font-display font-bold text-base text-white leading-[0.95] mb-3" data-id="element-1020">
          Save this list.
        </h3>
        <p className="text-[9px] text-white/60 leading-snug mb-4" data-id="element-1021">
          Tap save · share with a friend who scans at home.
        </p>
        <div className="bg-film-orange text-white text-[9px] font-mono px-3 py-1.5 uppercase tracking-wider" data-id="element-1022">
          Get Film Notes
        </div>
      </div>
}];